import { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import cn from 'classnames';

import { Container } from '@dansk-metal/ui';

import { EmptyState } from '@web/components/empty-state/empty-state';
import { HeroTitle } from '@web/components/hero-block/hero-title/hero-title';
import { Pagination } from '@web/components/pagination/pagination';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import { useGlobalContext } from '@web/layouts/page/context';
import TemplateLayout from '@web/layouts/template/template';

import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { useMemberEventsSearch } from '@web/templates/user-events-overview-page/hooks/use-member-events-search';

import { useShallowQueryParams } from '@web/hooks/use-query-param';

import { EventCardList } from '../../components/event-card-list/event-card-list';

import 'react-loading-skeleton/dist/skeleton.css';
import styles from './user-events-overview-page.module.scss';

export const userEventsOverviewPageContentAlias = 'userEventsOverviewPage' as const;

export type UserEventsOverviewPageData = UmbracoPage<
	typeof userEventsOverviewPageContentAlias,
	{
		title: string;
		subtitle: string;
		noFutureEventsBodyText: string;
		noFutureEventsTitle: string;
		noPastEventsBodyText: string;
		noPastEventsTitle: string;
	}
>;

export const limit = 10;

export enum TimeFilter {
	Upcoming = 'GreaterEqual',
	Past = 'LessThan'
}


const UserEventsOverviewPageTemplate: NextPage<{ pageData: UserEventsOverviewPageData }> = ({ pageData }) => {
	const router = useRouter();
	const { status } = useSession();
	const { pageContext } = useGlobalContext();
	const eventsPageUrl = pageContext?.eventsPageUrl;

	const {
		title,
		subtitle,
		noFutureEventsTitle,
		noFutureEventsBodyText,
		noPastEventsBodyText,
		noPastEventsTitle,
	} = pageData.properties;

	const [activeTab, setActiveTab] = useState<TimeFilter>(TimeFilter.Upcoming);

	const {
		eventsAuthQuery,
		pagination,
	} = useMemberEventsSearch({
		timeframe: activeTab,
		limit,
	});

	const events = eventsAuthQuery.data?.items;
	const pageFromUrl = parseInt(router.query.page as string, 10) || 1;
	const isEventsAvailable = events && Object.keys(events).length !== 0;
	const isLoading = eventsAuthQuery.isLoading || status === 'loading';

	const { updateUrlParam, urlStateLoaded } = useShallowQueryParams({
		routerQueryParamChanged: () => {
			pagination.setPage(pageFromUrl);
		},
	});

	// Handle page change
	useEffect(() => {
		if (!urlStateLoaded || !window) {
			return;
		}

		if (!isEventsAvailable) {
			updateUrlParam({ page: '' });
			return;
		}

		updateUrlParam({ page: pagination.page > 1 ? pagination.page.toString() : '' });
		window.scrollTo(0, 0);
	}, [isEventsAvailable, pagination.page, updateUrlParam, urlStateLoaded]);

	return (
		<TemplateLayout>
			{!!title && (
				<HeroTitle
					title={title}
					subtitle={subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container>
				<div className={styles.tab_list}>
					<div className={cn('swiper-wrapper', styles.swiper)}>
						<button
							className={cn(styles.tab, 'swiper-slide', { [styles.tab__active]: TimeFilter.Upcoming === activeTab })}
							onClick={() => setActiveTab(TimeFilter.Upcoming)}
						>
							Kommende
						</button>
						<button
							className={cn(styles.tab, 'swiper-slide', { [styles.tab__active]: TimeFilter.Past === activeTab })}
							onClick={() => setActiveTab(TimeFilter.Past)}
						>
							Tidligere
						</button>
					</div>
				</div>
			</Container>
			<div className={styles.events_container}>
				{isLoading && (
					<Container className={styles.container}>
						<ThemedSkeleton
							count={limit}
							containerClassName={styles.skeleton_theme_container}
							className={styles.skeleton_theme}
							inline
						/>
					</Container>
				)}
				{events && events.length !== 0 ? (
					<Container className={styles.container}>
						<ul className={styles.event_list}>
							<EventCardList
								events={events}
								baseUrl={eventsPageUrl}
							/>
						</ul>
						{pagination.maxPages > 1 && <Pagination {...pagination} />}
					</Container>
				) : (
					<EmptyState
						title={activeTab === TimeFilter.Upcoming ? noFutureEventsTitle : noPastEventsTitle}
						bodyText={activeTab === TimeFilter.Upcoming ? noFutureEventsBodyText : noPastEventsBodyText}
					/>
				)}
			</div>
		</TemplateLayout>
	);
};

export default UserEventsOverviewPageTemplate;
